/**
 * ProjectApiService - Gestisce le chiamate API per salvare e caricare progetti
 *
 * Backend API Documentation (verified working):
 * - Base URL: http://localhost:3004/api/Project/projects (NOTE: capital "P" in "Project")
 * - Auth: JWT cookie (macToken) automatico dopo login
 * - PUT /api/Project/projects/{guid} - Salva o aggiorna progetto (201 created / 200 updated)
 * - GET /api/Project/projects - Ottieni tutti i progetti utente
 * - GET /api/Project/projects/{guid} - Ottieni singolo progetto
 * - DELETE /api/Project/projects/{guid} - Elimina progetto
 *
 * ✅ Backend verified: PUT /api/Project/projects/{guid} works correctly
 *
 * ============================================================================
 * 🏗️ ARCHITECTURE: FLAT vs NESTED Data Structure
 * ============================================================================
 *
 * **WHY TWO FORMATS EXIST:**
 * - Backend database stores: `{ asphalt: {...}, airport: {...} }` (NESTED)
 * - Frontend components expect: `{ roadInfo, trafficLoad, ... }` (FLAT)
 *
 * **CRITICAL TRANSFORMATION POINTS:**
 * 1. Backend → Redux: `ensureFlatStructure()` in `transformProjectFromBackend()`
 * 2. Redux → Backend: `BackendPayloadService.preparePayload()`
 *
 * **CRITICAL RULES:**
 * - ⚠️ Redux MUST ALWAYS store FLAT format
 * - ⚠️ NEVER update Redux with nested structure (causes data loss bug)
 * - ⚠️ All backend loads MUST use `ensureFlatStructure()` for type-safe transformation
 * - ⚠️ Use type guards from `ProjectDataStructure.ts` to ensure safety
 *
 * **TYPE SAFETY:**
 * See `src/app/types/ProjectDataStructure.ts` for:
 * - `isNestedStructure()` - Detect format
 * - `ensureFlatStructure()` - Guarantee FLAT output
 * - `validateReduxStructure()` - Runtime checks
 *
 * **EXCEPTIONS:**
 * - Railway: Uses `.default` wrapper (legacy architecture, see PD3)
 * - Geogrid: Already flat (no method key)
 *
 * **RELATED FILES:**
 * - `ProjectDataStructure.ts` - Type guards and safe accessors
 * - `BackendPayloadService.ts` - FLAT → NESTED for API calls
 * - `useProjectSubmission.ts` - Redux updates after save/execute
 * - `InputForm.tsx` - Form data → Redux (already FLAT)
 */

import { IGeogridData, IProject } from '../api/IApiResponse';
import { logDebug, logWarn } from '../utils/utils';
import httpClient from '../../apiService/httpClient';
import { ensureFlatStructure } from '../types/ProjectDataStructure';
import { BackendPayloadService } from './BackendPayloadService';
import { resolveProjectSchemas } from './ProjectTypeService';
import { normalizeProjectData } from './WireValueNormalizer';
import { migrateLegacyGeogridSafetyFactor } from './GeogridSafetyFactor';
import { unit as unitEnum } from '../enum/project';

// Base URL for projects API (verified from Swagger: capital "P" in "Project")
const PROJECT_API_BASE = process.env.REACT_APP_API_BASEURL || 'http://localhost:3004';
const PROJECT_API_PATH = '/api/Project/projects';

/**
 * Check if user has valid JWT cookie, perform login if missing
 *
 * Environment-based authentication logic:
 * - Local development: Calls /api/auth/login with test credentials
 * - Dev/Prod (cloud): Expects JWT from eDesign Identity, throws error if missing
 */
const ensureAuthenticated = async (): Promise<void> => {
    // Get environment - defaults to 'local' if not set
    const environment = process.env.REACT_APP_ENVIRONMENT || 'local';

    if (environment === 'local') {
        // LOCAL DEVELOPMENT: Check if macToken exists (not HttpOnly in local)
        const checkCookie = (name: string) => {
            const cookie = document.cookie.split('; ').find(row => row.startsWith(name));
            return !!cookie;
        };

        if (!checkCookie('macToken')) {
            // Call backend login endpoint with test credentials
            logDebug('ProjectApiService', '⚠️ No JWT cookie found - performing local login...');
            try {
                const response = await httpClient.postAsync<{ username: string; password: string }, { token: string }>({
                    url: '/api/auth/login',
                    baseURL: PROJECT_API_BASE,
                    data: {
                        username: 'test',
                        password: 'test',
                    },
                });

                if (response.status === 200) {
                    logDebug('ProjectApiService', '✅ Local login successful - JWT cookie set by backend');
                } else {
                    throw new Error(`Login failed with status ${response.status}`);
                }
            } catch (error) {
                logDebug('ProjectApiService', '❌ Failed to login', error);
                throw new Error('Authentication failed - please refresh the page');
            }
        }
    } else {
        // DEV/PROD CLOUD: HttpOnly cookie (ID_groundmac-dev) cannot be checked via JavaScript
        // Cookie is sent automatically by browser - if API returns 401, user needs to login
        // No check needed here - let the API call fail naturally if authentication is missing
        logDebug('ProjectApiService', '✅ Production environment - relying on HttpOnly cookie from eDesign');
    }
};

/**
 * Backend project structure matching API requirements
 * See: Projects API Documentation
 */
export interface IBackendProject {
    guid: string;
    projectName: string;
    idApp: number;
    idUser: number;
    creationDate: string;
    lastModified: string;
    status: number;
    sharedUsers: number[];
    projectInfo: {
        coordinates: [number, number];
        projectOwner: string;
        designerFirmName: string;
        designerFirmCountry: string;
        productCountry: string | null;
        products: string[];
        data: {
            type?: string; // Optional - only present for non-standalone-geogrid projects
            notes?: string;
            unit?: string; // User's preferred display unit (SI/Imperial)
            method?: string; // Project method (bcr, static, giroud, leng, etc.)
            [key: string]: unknown; // Project-specific data (bcr, static, geoGrid, etc.)
        };
    };
}

/**
 * Response from GET /api/Project/projects (lista progetti)
 */
export type IBackendProjectsList = IBackendProject[];

/**
 * Transform IProject (frontend Redux) to IBackendProject (API format)
 *
 * ## Save Process:
 * 1. Takes IProject from Redux (values may be in Imperial or SI)
 * 2. Cleans up data for standalone geogrid projects
 * 3. Saves user preferences (unit, method) in data
 * 4. Returns IBackendProject for API
 *
 * ## Standalone Geogrid Handling:
 * For projects with type='geogrid' and data.geoGrid:
 * - Explicitly sets data.type = 'geogrid' (ensures correct detection on reload)
 * - Overwrites any previous type (e.g., 'container_yard' if saved from Container Yard)
 * - On reload, transformProjectFromBackend will see type='geogrid' and treat as standalone
 *
 * ## Data Saved:
 * - projectInfo.data: All project data (bcr, static, geoGrid, etc.)
 * - data.unit: User's display preference (SI/Imperial)
 * - data.method: Project method (bcr, static, giroud, etc. or '' for geogrid)
 *
 * Note: Values should already be converted to SI by useProjectSubmission before reaching here
 *
 * @param project - Frontend IProject from Redux
 * @returns Backend IBackendProject for API
 */
export const transformProjectToBackend = (project: IProject): IBackendProject => {
    logDebug('ProjectApiService', 'Transforming frontend project to backend format', { guid: project.guid });

    // For standalone geogrid projects, we need to set the correct 'type' field
    // Reason: Geogrid projects created from Container Yard may have type='container_yard' in data
    // We explicitly set type='geogrid' to ensure correct detection on reload
    const projectDataUnknown: unknown = project.projectInfo?.data;
    const hasGeoGrid = typeof projectDataUnknown === 'object' && projectDataUnknown !== null && 'geoGrid' in projectDataUnknown && Boolean((projectDataUnknown as { geoGrid?: unknown }).geoGrid);

    const isStandaloneGeogrid = project.type === 'geogrid' && hasGeoGrid;
    const cleanedData = { ...(project.projectInfo?.data || {}) };

    const projectTypeForBackend = isStandaloneGeogrid ? 'geogrid' : project.type;

    return {
        guid: project.guid,
        projectName: project.projectName,
        idApp: project.idApp,
        idUser: project.idUser, // Backend will override with authenticated user
        creationDate: project.creationDate,
        lastModified: project.lastModified,
        status: project.status,
        sharedUsers: Array.isArray(project.sharedUsers) ? (project.sharedUsers as number[]) : [],
        projectInfo: {
            coordinates: project.projectInfo?.coordinates || [0, 0],
            projectOwner: project.projectInfo?.projectOwner || project.projectInfo?.owner || '',
            designerFirmName: project.projectInfo?.designerFirmName || '',
            designerFirmCountry: project.projectInfo?.designerFirmCountry || '',
            productCountry: project.projectInfo?.productCountry || '', // Backend requires non-null string
            products: project.projectInfo?.products || [],
            data: {
                ...cleanedData,
                // Ensure project type is always saved.
                // Older projects may have no `projectInfo.data.type` in backend, but Redux always has `project.type`.
                type: projectTypeForBackend,
                // Note: Values are already converted to SI by useProjectSubmission before reaching here
                // But we save user's unit preference for display
                unit: project.unit, // ✅ User's preferred unit system (SI/Imperial) for display
                method: project.method, // ✅ Save method information
                // ✅ Explicitly preserve designerFirmCity and nrProject from cleanedData to ensure they're not lost
                // Use cleanedData values if present, otherwise fallback to empty string (not undefined to avoid stripping)
                designerFirmCity: cleanedData.designerFirmCity !== undefined ? cleanedData.designerFirmCity : '',
                nrProject: cleanedData.nrProject !== undefined ? cleanedData.nrProject : '',
            },
        },
    };
};

/**
 * Transform IBackendProject (API format) to IProject (frontend Redux)
 *
 * This is the AUTHORITATIVE source for project type detection. Do NOT override
 * the type returned by this function.
 *
 * ## Project Type Detection Rules:
 *
 * ### Standalone Geogrid Projects:
 * - Criteria: data.type='geogrid' AND HAS data.geoGrid, NO bcr/static/giroud/leng
 * - Result: type='geogrid', method=''
 * - Example: User creates a geogrid-only project
 * - Note: data.type='geogrid' is set explicitly by transformProjectToBackend
 *
 * ### Complex Projects (Container Yard, Railways, etc.):
 * - Criteria: HAS data.type field
 * - Result: type=data.type (e.g., 'railways', 'container_yard')
 * - Note: May also have data.geoGrid alongside other project data
 * - Example: Container Yard BCR project with optional geogrid
 *
 * ### Empty/New Projects:
 * - Criteria: NO data.type, NO data.geoGrid
 * - Result: type='geogrid' (fallback)
 *
 * ## Data Flow:
 * 1. Backend returns IBackendProject (values in SI units, nested structure)
 * 2. Extract saved unit/method from data.unit and data.method
 * 3. Determine project type (see rules above)
 * 4. **DENEST data structure** - Convert nested to flat for Redux
 * 5. Return IProject with:
 *    - type: Determined project type
 *    - unit: User's preferred display unit (SI/Imperial)
 *    - method: Saved method or '' for geogrid
 *    - projectInfo.data: **FLAT structure** (denested from backend)
 *
 * ⚠️ CRITICAL: Redux Structure Guarantee
 * This function GUARANTEES that projectInfo.data returned is in FLAT format.
 * The transformation uses type-safe `ensureFlatStructure()` from ProjectDataStructure.ts
 * which handles NESTED → FLAT conversion with proper type guards.
 *
 * **Type Safety:**
 * - Use `validateReduxStructure()` in components to verify FLAT format
 * - Use `isNestedStructure()` to detect format before processing
 * - See ProjectDataStructure.ts for type guards and safe accessors
 *
 * @param backendProject - Raw backend project from API
 * @returns Frontend IProject for Redux store with FLAT data structure
 *
 * @see ProjectDataStructure.ts for type-safe utilities
 */
export const transformProjectFromBackend = (backendProject: IBackendProject): IProject => {
    logDebug('ProjectApiService', 'Transforming backend project to frontend format', { guid: backendProject.guid });

    // Extract unit and method from projectInfo.data if available
    // Note: Backend always stores values in SI (Metric), but unit field stores user's display preference
    // FIX: Use unitEnum.SI ('Metric') instead of hardcoded 'SI' string for legacy projects without data.unit
    const savedUnit = (backendProject.projectInfo.data as any).unit || unitEnum.SI;
    const savedMethod = (backendProject.projectInfo.data as any).method || '';

    // Determine project type
    //
    // CRITICAL RULES:
    // 1. Standalone Geogrid: ONLY has data.geoGrid, NO bcr/static/giroud/leng/etc.
    // 2. Complex Projects (Container Yard, Railways, etc.): Has data.type AND may have data.geoGrid + other data
    //
    // Detection Logic:
    // - If data.type exists → Complex project (use explicit type)
    // - If NO data.type BUT has data.geoGrid → Standalone geogrid
    // - Otherwise → Fallback to geogrid (empty project)
    let projectType: any;
    const projectData = backendProject.projectInfo.data as any;
    const geoGridData = projectData.geoGrid;
    const explicitType = projectData.type;

    if (explicitType === 'geogrid' && geoGridData) {
        // Explicit geogrid standalone project
        // Validation: Should NOT have bcr/static/giroud/leng data
        const hasOtherProjectData = !!(projectData.bcr || projectData.static || projectData.giroud || projectData.leng);

        if (hasOtherProjectData) {
            logWarn('ProjectApiService', '⚠️ Inconsistent data: type="geogrid" but has other project data (bcr/static/etc). Treating as standalone geogrid anyway.', {
                guid: backendProject.guid,
                hasBcr: !!projectData.bcr,
                hasStatic: !!projectData.static,
                hasGiroud: !!projectData.giroud,
                hasLeng: !!projectData.leng,
            });
        }

        projectType = 'geogrid';
        const geogridType = geoGridData.type?.geogridType;
        logDebug('ProjectApiService', 'Detected standalone Geogrid project (data.type="geogrid")', {
            guid: backendProject.guid,
            geogridType,
            hasOtherData: hasOtherProjectData,
        });
    } else if (explicitType && explicitType !== 'geogrid') {
        // Has explicit type field (railways, container_yard, paved, etc.) - use it
        // These projects may also have geoGrid data alongside bcr/static/giroud/etc.
        projectType = explicitType;
        logDebug('ProjectApiService', `Project type from data.type: ${projectType}`, {
            guid: backendProject.guid,
            hasGeoGrid: !!geoGridData,
            hasBcr: !!projectData.bcr,
            hasStatic: !!projectData.static,
            hasGiroud: !!projectData.giroud,
        });
    } else if (geoGridData) {
        // LEGACY: No explicit type but has geoGrid - treat as standalone geogrid
        // This handles old projects saved before explicit type='geogrid' was added
        const hasOtherProjectData = !!(projectData.bcr || projectData.static || projectData.giroud || projectData.leng);

        if (hasOtherProjectData) {
            logWarn('ProjectApiService', '⚠️ LEGACY: geoGrid exists without type, but has other project data. Treating as standalone geogrid.', {
                guid: backendProject.guid,
                hasBcr: !!projectData.bcr,
                hasStatic: !!projectData.static,
                hasGiroud: !!projectData.giroud,
                hasLeng: !!projectData.leng,
            });
        }

        projectType = 'geogrid';
        const geogridType = geoGridData.type?.geogridType;
        logDebug('ProjectApiService', 'LEGACY: Detected standalone Geogrid project (has geoGrid, no data.type)', {
            guid: backendProject.guid,
            geogridType,
            hasOtherData: hasOtherProjectData,
        });
    } else {
        // No type, no geoGrid - fallback to geogrid (likely a new/empty project)
        projectType = 'geogrid';
        logDebug('ProjectApiService', 'Fallback to geogrid type (no data.type, no geoGrid)', { guid: backendProject.guid });
    }

    // Transform backend data structure to FLAT format for Redux
    // REASON: Backend saves in NESTED format { [method]: {...} }, but Redux must store FLAT { ...fields }
    // This prevents data loss bugs when components read from flat paths after save/reload
    // Uses type-safe ensureFlatStructure() from ProjectDataStructure.ts
    const rawBackendData = backendProject.projectInfo.data;
    console.log('🔄 [FLAT/NESTED] Backend→Redux BEFORE ensureFlatStructure:', {
        guid: backendProject.guid,
        method: savedMethod,
        unit: savedUnit,
        rawKeys: Object.keys(rawBackendData || {}),
        hasMethodKey: savedMethod && rawBackendData ? (rawBackendData as any)[savedMethod] !== undefined : false,
        structure: savedMethod && (rawBackendData as any)[savedMethod] ? 'NESTED' : 'FLAT',
    });

    const flattenedData = ensureFlatStructure(backendProject.projectInfo.data, savedMethod, projectType);
    // Legacy saved values (numbers in text fields, numeric strings in number fields) are normalized before
    // the UI fallbacks. Metadata sit at the root in both FLAT and NESTED shapes, so they survive ensureFlatStructure.
    const normalizedData = normalizeProjectData(
        flattenedData as Record<string, unknown>,
        { projectType, method: savedMethod, direction: 'inbound', ...resolveProjectSchemas(projectType, savedMethod) },
        'preserve'
    );
    // Single load point for every reader of a saved project: an FS saved with the old Railway/Airport GeoGrid prefill loads as 3
    const geoGrid = migrateLegacyGeogridSafetyFactor(projectType, normalizedData.geoGrid as IGeogridData | undefined);
    // The form shows '' for null/absent metadata; 0 and "0" must not collapse into it.
    const designerFirmCity = normalizedData.designerFirmCity ?? '';
    const nrProject = normalizedData.nrProject ?? '';

    console.log('✅ [FLAT/NESTED] Backend→Redux AFTER ensureFlatStructure:', {
        guid: backendProject.guid,
        flatKeys: Object.keys(flattenedData || {}).slice(0, 15),
        hasMethodKey: savedMethod && flattenedData ? (flattenedData as any)[savedMethod] !== undefined : false,
        structure: 'FLAT (ready for Redux)',
    });

    return {
        guid: backendProject.guid,
        projectName: backendProject.projectName,
        type: projectType, // EProjectType
        method: savedMethod, // Restore saved method
        unit: savedUnit, // Restore user's preferred display unit (SI/Imperial)
        mapImage: '',
        projectInfo: {
            coordinates: backendProject.projectInfo.coordinates,
            projectOwner: backendProject.projectInfo.projectOwner,
            owner: backendProject.projectInfo.projectOwner, // Duplicate for compatibility
            designerFirmName: backendProject.projectInfo.designerFirmName,
            designerFirmCountry: backendProject.projectInfo.designerFirmCountry,
            productCountry: backendProject.projectInfo.productCountry || undefined, // Convert null to undefined
            products: backendProject.projectInfo.products,
            data: {
                ...normalizedData,
                ...(geoGrid ? { geoGrid } : {}),
                // ✅ Explicitly set common properties with fallback chain
                designerFirmCity,
                nrProject,
            } as any, // ProjectData type - FLAT structure (denested from backend)
        },
        readOnly: false,
        hasBeenRan: false,
        idApp: backendProject.idApp,
        idUser: backendProject.idUser,
        creationDate: backendProject.creationDate,
        lastModified: backendProject.lastModified,
        country: '',
        language: '',
        status: backendProject.status,
        isCalculated: false,
        isValid: false,
        isApproved: false,
        lastSeen: backendProject.lastModified,
        sharedUsers: backendProject.sharedUsers,
        geogridHasBeenRan: false,
        geogridUserModified: false,
        disableRun: false,
        newProject: false, // Projects from backend are not new
    };
};

/**
 * Salva o aggiorna un progetto sul backend
 * PUT /api/Project/projects/{guid}
 *
 * @returns {Promise<{ success: boolean; status: 200 | 201; data?: IBackendProject; error?: string }>}
 * - status 201: Progetto creato
 * - status 200: Progetto aggiornato
 */
export const saveProjectToBackend = async (project: IProject): Promise<{ success: boolean; status: 200 | 201 | 400 | 500; data?: IBackendProject; error?: string }> => {
    try {
        // Ensure JWT token exists before making request
        await ensureAuthenticated();

        logDebug('ProjectApiService', `PUT /api/Project/projects/${project.guid}`, { projectName: project.projectName });

        // Sprint 1: Use orchestrated pipeline (handles unit conversion + nesting)
        const preparedProject = await BackendPayloadService.orchestratePayload(project);
        const backendProject = transformProjectToBackend(preparedProject);

        // 🔍 DEBUG: Log complete payload being sent to backend
        logDebug('ProjectApiService', '📤 PAYLOAD INVIATO AL BACKEND:', {
            guid: backendProject.guid,
            unit: backendProject.projectInfo.data.unit,
            method: backendProject.projectInfo.data.method,
            deckWidth: (backendProject.projectInfo.data as any).bcr?.load?.deckWidth,
            fullPayload: JSON.stringify(backendProject, null, 2),
        });

        // Use httpClient to benefit from auth interceptor (auto token generation on 401)
        const response = await httpClient.putAsync<IBackendProject, IBackendProject>({
            url: `${PROJECT_API_PATH}/${project.guid}`,
            baseURL: PROJECT_API_BASE,
            data: backendProject,
        });

        if (response.status === 201) {
            logDebug('ProjectApiService', '✅ Project created (201)', { guid: project.guid });
            return { success: true, status: 201, data: response.data };
        } else if (response.status === 200) {
            logDebug('ProjectApiService', '✅ Project updated (200)', { guid: project.guid });
            return { success: true, status: 200, data: response.data };
        } else {
            logDebug('ProjectApiService', `❌ Save failed (${response.status})`, { error: response.statusText });
            return { success: false, status: response.status as 400 | 500, error: response.statusText };
        }
    } catch (error) {
        logDebug('ProjectApiService', '❌ Network error during save', error);
        return { success: false, status: 500, error: String(error) };
    }
};

/**
 * Carica tutti i progetti dell'utente autenticato
 * GET /api/Project/projects
 *
 * @returns {Promise<{ success: boolean; data?: IBackendProject[]; error?: string }>}
 */
export const loadProjectsFromBackend = async (): Promise<{ success: boolean; data?: IBackendProject[]; error?: string }> => {
    try {
        // Ensure JWT token exists before making request
        await ensureAuthenticated();

        logDebug('ProjectApiService', 'GET /api/Project/projects');

        const response = await httpClient.getAsync<IBackendProjectsList>({
            url: PROJECT_API_PATH,
            baseURL: PROJECT_API_BASE,
        });

        if (response.status === 200) {
            const data = response.data || [];
            logDebug('ProjectApiService', `✅ Loaded ${data.length} projects`);
            return { success: true, data };
        } else {
            logDebug('ProjectApiService', `❌ Load failed (${response.status})`, { error: response.statusText });
            return { success: false, error: response.statusText };
        }
    } catch (error) {
        logDebug('ProjectApiService', '❌ Network error during load', error);
        return { success: false, error: String(error) };
    }
};

/**
 * Carica un singolo progetto per GUID
 * GET /api/Project/projects/{guid}
 *
 * @returns {Promise<{ success: boolean; data?: IBackendProject; error?: string; status?: number }>}
 * - 200: Success
 * - 404: Project not found
 * - 403: Not authorized
 */
export const loadProjectByGuid = async (guid: string): Promise<{ success: boolean; data?: IBackendProject; error?: string; status?: number }> => {
    try {
        // Ensure JWT token exists before making request
        await ensureAuthenticated();

        logDebug('ProjectApiService', `GET /api/Project/projects/${guid}`);

        const response = await httpClient.getAsync<IBackendProject>({
            url: `${PROJECT_API_PATH}/${guid}`,
            baseURL: PROJECT_API_BASE,
        });

        if (response.status === 200) {
            logDebug('ProjectApiService', '✅ Project loaded', { guid });

            return { success: true, data: response.data, status: 200 };
        } else if (response.status === 404) {
            logDebug('ProjectApiService', '❌ Project not found (404)', { guid });
            return { success: false, error: 'Project not found', status: 404 };
        } else if (response.status === 403) {
            logDebug('ProjectApiService', '❌ Not authorized (403)', { guid });
            return { success: false, error: 'Not authorized', status: 403 };
        } else {
            logDebug('ProjectApiService', `❌ Load failed (${response.status})`, { error: response.statusText });
            return { success: false, error: response.statusText, status: response.status };
        }
    } catch (error) {
        logDebug('ProjectApiService', '❌ Network error during load', error);
        return { success: false, error: String(error), status: 500 };
    }
};

/**
 * Elimina un progetto dal backend
 * DELETE /api/Project/projects/{guid}
 *
 * @returns {Promise<{ success: boolean; error?: string; status?: number }>}
 * - 204: Deleted successfully
 * - 404: Not found
 * - 403: Not authorized
 */
export const deleteProjectFromBackend = async (guid: string): Promise<{ success: boolean; error?: string; status?: number }> => {
    try {
        // Ensure JWT token exists before making request
        await ensureAuthenticated();

        logDebug('ProjectApiService', `DELETE /api/Project/projects/${guid}`);

        const response = await httpClient.deleteAsync({
            url: `${PROJECT_API_PATH}/${guid}`,
            baseURL: PROJECT_API_BASE,
        });

        if (response.status === 204) {
            logDebug('ProjectApiService', '✅ Project deleted (204)', { guid });
            return { success: true, status: 204 };
        } else if (response.status === 404) {
            logDebug('ProjectApiService', '❌ Project not found (404)', { guid });
            return { success: false, error: 'Project not found', status: 404 };
        } else if (response.status === 403) {
            logDebug('ProjectApiService', '❌ Not authorized (403)', { guid });
            return { success: false, error: 'Not authorized', status: 403 };
        } else {
            logDebug('ProjectApiService', `❌ Delete failed (${response.status})`, { error: response.statusText });
            return { success: false, error: response.statusText, status: response.status };
        }
    } catch (error) {
        logDebug('ProjectApiService', '❌ Network error during delete', error);
        return { success: false, error: String(error), status: 500 };
    }
};

/**
 * Utility: Generate new GUID for projects
 */
export const generateProjectGuid = (): string => {
    return crypto.randomUUID();
};
